import React, { useMemo } from "react";
import { Show } from "@refinedev/antd";
import { useNavigation, useShow } from "@refinedev/core";
import {
  Typography,
  Image,
  Space,
  Tag,
  Descriptions,
  Row,
  Col,
  Card,
  Button,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { getStatusMeta } from "./shared";

const { Title, Text, Paragraph } = Typography;

type MovieRecord = {
  id?: string;
  title?: string;
  original_title?: string | null;
  tagline?: string | null;
  overview?: string | null;
  poster_url?: string | null;
  backdrop_url?: string | null;
  video_url?: string | null;
  movies_videos?: { id?: string; status?: string | null; cloudflare_uid?: string | null }[];
  release_date?: string | null;
  runtime?: number | null;
  vote_average?: number | null;
  vote_count?: number | null;
  view_count?: number | null;
  popularity?: number | null;
  preview_duration?: number | null;
  language?: string | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  movie_categories?: { categories?: { name?: string | null } }[];
};

export const MovieShow: React.FC = () => {
  const { queryResult } = useShow<MovieRecord>({
    meta: { select: "*, movie_categories(categories(name)), movies_videos(id, status, cloudflare_uid)" },
  });
  const { data, isLoading } = queryResult;
  const record = data?.data;
  const { list, edit } = useNavigation();

  const categories = useMemo(
    () =>
      (record?.movie_categories || [])
        .map((item) => item?.categories?.name)
        .filter(Boolean) as string[],
    [record?.movie_categories]
  );

  const statusMeta = getStatusMeta(record?.status);
  const releaseDisplay = record?.release_date
    ? dayjs(record.release_date).format("YYYY-MM-DD")
    : "未设置";
  const runtimeDisplay = record?.runtime ? `${record.runtime} 分钟` : "未设置";
  const hasVideo = Boolean(record?.video_url) || Boolean(record?.movies_videos?.length);
  const videoUid = record?.movies_videos?.[0]?.cloudflare_uid;
  const videoStatus = record?.movies_videos?.[0]?.status;

  return (
    <Show
      isLoading={isLoading}
      headerButtons={() => (
        <Space>
          <Button onClick={() => list("movies")}>返回列表</Button>
          <Button
            type="primary"
            disabled={!record?.id}
            onClick={() => record?.id && edit("movies", record.id)}
          >
            编辑
          </Button>
        </Space>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card bordered={false} bodyStyle={{ padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <div
                style={{
                  width: "100%",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#0f172a",
                }}
              >
                <Image
                  src={record?.poster_url || "https://placehold.co/1280x720?text=暂无封面"}
                  alt={record?.title}
                  preview={record?.poster_url ? { mask: "查看大图" } : false}
                  style={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                  }}
                  fallback="https://placehold.co/1280x720?text=封面加载失败"
                />
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Space size="small" wrap>
                  <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                  <Tag>上映 {releaseDisplay}</Tag>
                  <Tag>{runtimeDisplay}</Tag>
                </Space>

                <Title level={3} style={{ marginBottom: 4 }}>
                  {record?.title || "未填写标题"}
                </Title>
                {record?.original_title && <Text type="secondary">{record.original_title}</Text>}
                {record?.tagline && (
                  <Paragraph type="secondary" style={{ marginTop: 4 }}>
                    “{record.tagline}”
                  </Paragraph>
                )}

                <Space size={[6, 6]} wrap>
                  {categories.length ? (
                    categories.map((name) => (
                      <Tag key={name} color="blue">
                        {name}
                      </Tag>
                    ))
                  ) : (
                    <Tag color="default">未分组</Tag>
                  )}
                </Space>

                <Divider style={{ margin: "12px 0" }} />

                <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 100 }}>
                  <Descriptions.Item label="语言">
                    {record?.language || "未设置"}
                  </Descriptions.Item>
                  <Descriptions.Item label="试看时长">
                    {record?.preview_duration ? `${record.preview_duration} 秒` : "未设置"}
                  </Descriptions.Item>
                  <Descriptions.Item label="评分">
                    {typeof record?.vote_average === "number"
                      ? `${record.vote_average.toFixed(1)} / 10`
                      : "暂无评分"}
                  </Descriptions.Item>
                  <Descriptions.Item label="人气 / 播放">
                    {`${record?.popularity ?? 0} / ${record?.view_count ?? 0}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="投票数">
                    {record?.vote_count ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="更新于">
                    {record?.updated_at
                      ? dayjs(record.updated_at).format("YYYY-MM-DD HH:mm")
                      : "未记录"}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="媒资状态" bordered={false}>
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Space size={[6, 6]} wrap>
                  <Tag color={record?.poster_url ? "green" : "red"}>
                    {record?.poster_url ? "封面已上传" : "缺少封面"}
                  </Tag>
                  <Tag color={record?.backdrop_url ? "blue" : "red"}>
                    {record?.backdrop_url ? "竖版背景就绪" : "缺少竖版背景"}
                  </Tag>
                  <Tag color={hasVideo ? "green" : "orange"}>
                    {hasVideo ? "视频已上传" : "无视频"}
                  </Tag>
                  {videoStatus && (
                    <Tag color={videoStatus === "ready" ? "green" : "gold"}>
                      转码状态：{videoStatus}
                    </Tag>
                  )}
                </Space>

                <Row gutter={12}>
                  <Col span={12}>
                    <Image
                      src={record?.poster_url || "https://placehold.co/640x360?text=暂无封面"}
                      alt="横版封面"
                      style={{ width: "100%", borderRadius: 8 }}
                      preview={record?.poster_url ? { mask: "查看大图" } : false}
                    />
                  </Col>
                  <Col span={12}>
                    <Image
                      src={record?.backdrop_url || "https://placehold.co/720x1280?text=暂无竖版背景"}
                      alt="竖版背景"
                      style={{ width: "100%", borderRadius: 8, aspectRatio: "9 / 16", objectFit: "cover" }}
                      preview={record?.backdrop_url ? { mask: "查看大图" } : false}
                    />
                  </Col>
                </Row>

                {record?.video_url ? (
                  <Button type="link" href={record.video_url} target="_blank" rel="noopener noreferrer">
                    打开播放链接
                  </Button>
                ) : (
                  hasVideo &&
                    videoUid && (
                      <Button type="dashed" disabled>
                        已上传（Cloudflare UID: {videoUid}），等待播放地址
                      </Button>
                    )
                )}
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="元数据与运维" bordered={false}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="状态">
                  <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="上映日期">
                  {releaseDisplay}
                </Descriptions.Item>
                <Descriptions.Item label="时长">
                  {runtimeDisplay}
                </Descriptions.Item>
                <Descriptions.Item label="语言">
                  {record?.language || "未设置"}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {record?.created_at
                    ? dayjs(record.created_at).format("YYYY-MM-DD HH:mm")
                    : "未记录"}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间">
                  {record?.updated_at
                    ? dayjs(record.updated_at).format("YYYY-MM-DD HH:mm")
                    : "未记录"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card bordered={false} title="简介">
          <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
            {record?.overview || "暂无简介"}
          </Paragraph>
        </Card>
      </Space>
    </Show>
  );
};
